import { useTranslate } from '@refinedev/core';
import { CollectionKind, DisplayMode } from '@/api/generated/schemas';
import type { CollectionOut } from '@/api/generated/schemas';
import { GUTTER } from '@/components/mobile_preview/constants';
import { effectiveDisplayMode } from '@/components/user_catalog/sections/sectionDisplay';
import { useVisibleOnce } from '@/hooks/useVisibleOnce';
import { HomePreviewShelfBanner } from './HomePreviewShelfBanner';
import { HomePreviewShelfTiles } from './HomePreviewShelfTiles';

interface HomePreviewShelfProps {
  collectionId: number;
  name?: string | null;
  kind?: CollectionKind;
  /** The saved mode. What is labelled is what the app will really draw,
   * which is not the same thing — the server degrades some picks. */
  displayMode?: DisplayMode;
  /** The resolved collection, for that degradation check. */
  collection?: CollectionOut;
}

/** One home section as the feed draws it. Banner collections carry creatives,
 * every other kind resolves to publications. */
export const HomePreviewShelf = ({
  collectionId,
  name,
  kind,
  displayMode,
  collection,
}: HomePreviewShelfProps) => {
  const t = useTranslate();
  const isBanner = kind === CollectionKind.banner;
  const isPersonal = kind === CollectionKind.personal;
  // No category id to compare against: the editor locks the root's own "All"
  // row to regular, so the draft can never carry another mode for it.
  const mode = effectiveDisplayMode(
    displayMode,
    collectionId,
    null,
    collection
  );
  const { ref, visible } = useVisibleOnce<HTMLDivElement>();

  return (
    <div ref={ref} style={{ marginTop: 24 }}>
      <div style={{ padding: `0 ${GUTTER}px`, marginBottom: 10 }}>
        {kind && (
          <div
            style={{
              fontSize: 11,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              opacity: 0.6,
              marginBottom: 2,
            }}
          >
            {t(`user_catalog.collections.kind.${kind}`)}
            {/* Regular is the default look — naming it would put a label on
                every shelf and single out none. */}
            {mode !== DisplayMode.regular &&
              ` · ${t(`user_catalog.common.display_mode.${mode}`)}`}
          </div>
        )}
        <div
          style={{ fontFamily: 'Georgia, serif', fontSize: 17, minHeight: 22 }}
        >
          {name ?? ''}
        </div>
      </div>

      {isPersonal ? (
        <div style={{ padding: `0 ${GUTTER}px`, fontSize: 12, opacity: 0.6 }}>
          {t('user_catalog.home_page.preview.personal_shelf')}
        </div>
      ) : isBanner ? (
        <HomePreviewShelfBanner collectionId={collectionId} enabled={visible} />
      ) : (
        <HomePreviewShelfTiles collectionId={collectionId} enabled={visible} />
      )}
    </div>
  );
};
